import { useCallback, useState, useEffect } from "react";
import { useExportVideoMutation, useDownloadVideoQuery } from "services/api";
import websocketService from "services/websocket";
import { API_URL } from "constants";
import { formatTime } from "utilities/time";

export const useExportVideo = ({ projectId, selectedSegments }) => {
  const [isExportingVideo, setIsExportingVideo] = useState(false);
  const [exportVideoProgress, setExportVideoProgress] = useState(0);
  const [exportVideoResult, setExportVideoResult] = useState(null);
  const [exportVideoError, setExportVideoError] = useState(null);
  const [shouldDownloadVideo, setShouldDownloadVideo] = useState(false);

  const { data: downloadData } = useDownloadVideoQuery(
    { projectId },
    { skip: !shouldDownloadVideo }
  );
  const [exportVideo] = useExportVideoMutation();

  const handleExportVideo = useCallback(async () => {
    if (!selectedSegments || selectedSegments.length === 0) return;

    try {
      setIsExportingVideo(true);
      await exportVideo({
        projectId,
        sequence: selectedSegments.map((segment) => ({
          filename: segment.filename,
          in: formatTime(segment.start),
          out: formatTime(segment.end),
        })),
      }).unwrap();

      setExportVideoProgress(0);
      setExportVideoResult(null);
      setExportVideoError(null);

      // Join the project room to receive updates
      websocketService.joinProject(projectId);
    } catch (error) {
      console.error("Failed to export to MP4:", error);
      setExportVideoError("Failed to export to MP4");
      setIsExportingVideo(false);
    }
  }, [exportVideo, projectId, selectedSegments]);

  const handleStarted = useCallback(() => {
    setExportVideoProgress(0);
  }, []);

  const handleProgress = useCallback((data) => {
    setExportVideoProgress(data.progress);
  }, []);

  const handleComplete = useCallback(
    (data) => {
      setExportVideoProgress(100);
      setExportVideoResult(data);
      setIsExportingVideo(false);
      setExportVideoError(null);

      if (data.video_url) {
        const link = document.createElement("a");
        link.href = `${API_URL}${data.video_url}`;
        link.download = "output_video.mp4";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Clean up websocket connection
      websocketService.removeEventListener("video_progress", handleProgress);
      websocketService.removeEventListener(
        "video_generation_complete",
        handleComplete
      );
      websocketService.removeEventListener(
        "video_processing_started",
        handleStarted
      );
      websocketService.leaveProject(projectId);
    },
    [handleProgress, handleStarted, projectId]
  );

  // Handle automatic download when downloadData is available
  useEffect(() => {
    if (downloadData) {
      // Create a URL for the blob
      const url = URL.createObjectURL(downloadData);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.download = "output_video.mp4";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      URL.revokeObjectURL(url);

      // Reset download state
      setShouldDownloadVideo(false);
    }
  }, [downloadData]);

  // Set up websocket event listeners when export starts
  useEffect(() => {
    if (!isExportingVideo) return;

    websocketService.addEventListener(
      "video_processing_started",
      handleStarted
    );
    websocketService.addEventListener("video_progress", handleProgress);
    websocketService.addEventListener(
      "video_generation_complete",
      handleComplete
    );

    return () => {
      websocketService.removeEventListener(
        "video_processing_started",
        handleStarted
      );
      websocketService.removeEventListener("video_progress", handleProgress);
      websocketService.removeEventListener(
        "video_generation_complete",
        handleComplete
      );
      websocketService.leaveProject(projectId);
    };
  }, [
    handleComplete,
    handleProgress,
    handleStarted,
    isExportingVideo,
    projectId,
  ]);

  return {
    isExportingVideo,
    exportVideoProgress,
    exportVideoResult,
    exportVideoError,
    handleExportVideo,
    setShouldDownloadVideo,
  };
};

export default useExportVideo;
